import type { DomainServiceSettings } from '@/decorators/types'
import { REFLECT_METADATA } from '@/decorators/types'
import type { BaseSearcherSettings } from '@/services/search/types'
import type OrmBaseRepository from '@/data/repositories/ormBaseRepository'
import type BaseModel from '@/data/models/BaseModel'

export function DomainSettings(settings: DomainServiceSettings) {
  return function _<T extends { new (...args: any[]): Record<string, any> }>(constructor: T) {
    // save metadata on the original constructor
    Reflect.defineMetadata(REFLECT_METADATA.DomainSettings, settings, constructor)

    // add getDomainSettings method to prototype instead of creating new class
    constructor.prototype.getDomainSettings = function () {
      return settings
    }

    // return the original constructor without changes
    return constructor
  }
}

export function Searchable(searchSettings: BaseSearcherSettings) {
  type Item = Record<string, string | undefined>

  return function _<T extends { new (...args: any[]): Record<string, any> }>(constructor: T) {
    // add methods to prototype instead of creating a new class
    constructor.prototype.getSearchSettings = function (): BaseSearcherSettings {
      return searchSettings
    }

    constructor.prototype.search = function (value: string, queryValue?: any): void {
      // initialize emptyState if it's not initialized
      if (this.emptyState === undefined) {
        this.emptyState = false
      }

      const repo: OrmBaseRepository<BaseModel, string> = constructor.prototype.getDomainRepository.call(this)

      const settings = this.getSearchSettings() as BaseSearcherSettings
      const searchFields = settings.supportedSearchFields
      const queryField = settings.supportedQueryField

      const filteredItemsQueryBuilder = repo.query()
      searchFields.forEach((field: string): void => {
        filteredItemsQueryBuilder.where(
          (item) => !!item[field] && item[field]?.toLowerCase().includes(value.toLowerCase()),
        )
      })
      if (queryField && queryValue) {
        filteredItemsQueryBuilder.where((item: Item) => item[queryField] === queryValue)
      }
      const filteredItems = filteredItemsQueryBuilder.all()
      this.emptyState = filteredItems.length === 0

      const hiddenItemsQueryBuilder = repo.query()
      searchFields.forEach((field: string): void => {
        hiddenItemsQueryBuilder.where(
          (item: Item) => !!item[field] && !item[field]?.toLowerCase().includes(value.toLowerCase()),
        )
      })
      if (queryField && queryValue) {
        hiddenItemsQueryBuilder.where((item: Item) => item[queryField] === queryValue)
      }
      const hiddenItems = hiddenItemsQueryBuilder.all()

      hiddenItems.forEach((item) => {
        item.$update({ isVisible: false })
      })

      filteredItems.forEach((item) => {
        item.$update({ isVisible: true })
      })
    }

    constructor.prototype.isEmptyState = function (): boolean {
      // initialize emptyState if it's not initialized
      if (this.emptyState === undefined) {
        this.emptyState = false
      }
      return this.emptyState
    }

    constructor.prototype.setIsEmptyState = function (value: boolean): void {
      this.emptyState = value
    }

    // return the original constructor without changes
    return constructor
  }
}
